class CartDrawerClearanceAddon extends HTMLElement {
  constructor() {
    super()

  }
  connectedCallback() {
    const metafieldSizes = window.TcSizePreferences?.parseMetafieldSizes(this.dataset.metafieldSizes)
    this.preferredSizes = window.TcSizePreferences?.getEffectiveSizes(metafieldSizes)
    this.handleBundleAdd = this.handleBundleAdd.bind(this)
    this.renderBundle()
  }

  disconnectedCallback() {
    this.addBtn?.removeEventListener('click', this.handleBundleAdd)
    this.addBtn = null
  }

  // Singles/family products are filtered to matching-variant only (they're colorways of
  // the same style, so every match is a legitimate pack candidate). Curated packs (from
  // shop.metaobjects.clearance_packs) mirror the "Quick Bundles" gating in
  // getSelectableItems(card, selector, true) on sections/clearance-v3.liquid: a pack is
  // only offered if EVERY one of its products resolves a matching variant — no partial
  // packs, and no padding/truncating to 3 — so the first fully-available pack (in
  // metaobject order) is returned whole, at its own product count. Accessories aren't
  // colorways of one another, so only the single best-matching accessory is kept —
  // otherwise unrelated accessory products (a cap, a backpack, a scarf) would get bundled
  // together as if they were a "3-pack" of the same item.
  parseProducts() {
    const script = this.querySelector('[clearance-addon-json-products]')
    if (!script) return []
    try {
      const products = JSON.parse(script.textContent || '[]')
      const singles = products.filter(
        (product) => !product.accessory && !product.pack && this.getVariantForSize(product)
      )
      if (singles.length) return singles

      // Grouped by pack_key (the metaobject's own handle), not pack_name (its display
      // name) — two separate clearance_packs entries can share the same merchandiser-given
      // name, and grouping by name would silently merge their products into one false pack.
      const packGroups = new Map()
      for (const product of products) {
        if (!product.pack) continue
        const key = product.pack_key || product.pack_name || ''
        if (!packGroups.has(key)) packGroups.set(key, [])
        packGroups.get(key).push(product)
      }
      for (const packProducts of packGroups.values()) {
        if (packProducts.length && packProducts.every((product) => this.getVariantForSize(product))) {
          return packProducts
        }
      }

      const accessories = products.filter((product) => product.accessory)
      const bestAccessory = this.pickAccessory(accessories)
      return bestAccessory ? [bestAccessory] : []
    } catch (error) {
      console.warn('CartDrawerClearanceAddon....unable to parse clearance products', error)
      return []
    }
  }

  // Prefer whichever of the rendered accessory candidates has a variant matching the
  // shopper's own saved size preference. If there's no saved preference at all, don't
  // guess a sized item's only-in-stock size (e.g. a boxer brief that only has 2XL
  // left) — fall back to genuinely one-size items only. If a preference exists but
  // just doesn't match anything in stock, fall back to any available candidate.
  pickAccessory(accessories) {
    const bestMatch = accessories.find((product) => this.getVariantForPreferredSize(product.variants || []))
    if (bestMatch) return bestMatch

    if (!this.preferredSizes?.top) {
      return (
        accessories.find(
          (product) => (product.variants || []).length === 1 && this.getFirstAvailableVariant(product.variants)
        ) || null
      )
    }

    return accessories.find((product) => this.getFirstAvailableVariant(product.variants || [])) || null
  }

  // A 3-pack needs one variant per product matching the size already in the cart —
  // read straight off data-reference-variant-title instead of the saved size preferences.
  // Curated packs (shop.metaobjects.clearance_packs) aren't tied to the reference item's
  // family, but a shopper adding one on top of the item already in their cart still most
  // likely wants that same size first — so packs try the reference size, then fall back
  // to the shopper's own saved/preferred size (localStorage, then the customer metafield
  // — see window.TcSizePreferences). Deliberately no further fallback to "any available
  // variant" here: a pack product that matches neither size isn't a real match, and
  // parseProducts() needs that null to correctly exclude it (or skip the whole pack and
  // move to the next one / the accessory tier) instead of silently substituting the wrong
  // size. Accessories aren't sized to the reference item at all, so they skip straight to
  // preferred size, with an "any available" fallback since a single accessory is picked
  // via pickAccessory()'s own looser rules, not this per-product match.
  getVariantForSize(product) {
    const variants = product?.variants || []
    if (!variants.length) return null

    // A product with only one variant total (e.g. one-size) just needs to be in stock.
    if (variants.length === 1) {
      return variants[0].available ? variants[0] : null
    }

    if (product.pack) {
      return (
        this.matchVariantToSize(variants, this.dataset.referenceVariantTitle) ||
        this.getVariantForPreferredSize(variants)
      )
    }

    if (product.accessory) {
      return this.getVariantForPreferredSize(variants) || this.getFirstAvailableVariant(variants)
    }

    return this.matchVariantToSize(variants, this.dataset.referenceVariantTitle)
  }

  // Same top-size match order as restoreSizePreference() in liquid-pdp.js, using
  // whichever size was saved (localStorage 'tc_sizes', or the customer metafield).
  getVariantForPreferredSize(variants) {
    return this.matchVariantToSize(variants, this.preferredSizes?.top)
  }

  // Shared by getVariantForSize() (reference-item size) and getVariantForPreferredSize()
  // (shopper's saved size) — both just need an available variant whose option/title
  // aliases to a given size label. Mirrors variantMatchesSize() in
  // sections/clearance-v3.liquid: a combined option/title like "Black / M" won't equal
  // "M" outright, so each value is also split on /,- and checked part-by-part — without
  // this, packs whose products carry a color+size variant (not just size) would silently
  // fail to match on every product and get excluded by the all-or-nothing pack gate.
  matchVariantToSize(variants, sizeLabel) {
    if (!sizeLabel) return null

    const normalize = window.TcSizePreferences?.normalizeSize || ((value) => String(value || '').trim().toUpperCase())
    const aliases = new Set(
      (window.TcSizePreferences?.getTopSizeAliases?.(sizeLabel) || [sizeLabel]).map((alias) => normalize(alias))
    )

    return (
      variants.find((variant) => {
        if (!variant.available) return false
        const values = [variant.option1, variant.option2, variant.option3, variant.title].filter(Boolean)
        return values.some((value) => {
          const normalizedValue = normalize(value)
          if (aliases.has(normalizedValue)) return true
          const parts = normalizedValue
            .split(/[/,-]/)
            .map((part) => part.trim())
            .filter(Boolean)
          return parts.some((part) => aliases.has(part))
        })
      }) || null
    )
  }

  getFirstAvailableVariant(variants) {
    return variants.find((variant) => variant.available) || null
  }

  // Same fill rule as getStyleBundleProducts in sections/clearance-v3.liquid: pad out
  // to 3 items by repeating when there aren't enough distinct products in the reference size.
  // `products` is already filtered to matching-variant products by parseProducts().
  buildThreePack(products) {
    if (!products.length) return []
    if (products.length === 1) return [products[0], products[0], products[0]]
    if (products.length === 2) return [products[0], products[0], products[1]]
    return products.slice(0, 3)
  }

  resolveProductImage(product) {
    return product.featured_image || null
  }

  // Recovers the active market's currency format from data-money-probe — a price the host
  // snippet rendered through Liquid's own `money` filter. Because the probe's cents amount
  // is also known, the digit count alone says whether the market shows a minor unit, and
  // the remaining characters give the symbol placement and separators. Deliberately not
  // window.Shopify.formatMoney: that is pinned to the USD `${{amount}}` format in
  // global.js, so it prints "$" on every market. Same parser as moneyParts() in
  // clearance-v3.js. Memoized — the probe cannot change without a re-render.
  moneyParts() {
    if (this.parsedMoneyParts) return this.parsedMoneyParts

    const probe = this.dataset.moneyProbe || ''
    const probeCents = Number(this.dataset.moneyProbeCents)
    const firstDigit = probe.search(/\d/)
    const lastDigit = probe.length - 1 - probe.split('').reverse().join('').search(/\d/)

    // USD default, matching the previous behaviour if the probe is missing or unparseable.
    this.parsedMoneyParts = { prefix: '$', suffix: '', thousands: ',', decimal: '.', precision: 2 }

    if (!Number.isFinite(probeCents) || probeCents <= 0 || firstDigit < 0 || lastDigit < firstDigit) {
      return this.parsedMoneyParts
    }

    const core = probe.slice(firstDigit, lastDigit + 1)
    const precision = core.replace(/\D/g, '').length - String(Math.round(probeCents / 100)).length
    if (precision !== 0 && precision !== 2) return this.parsedMoneyParts

    // With a minor unit, the last two digits are always preceded by the decimal separator,
    // which leaves the grouping separator as the last non-digit before it.
    const decimal = precision === 2 ? core.charAt(core.length - 3) : ''
    if (precision === 2 && (!decimal || /\d/.test(decimal))) return this.parsedMoneyParts

    const whole = precision === 2 ? core.slice(0, core.length - 3) : core
    const separators = whole.replace(/\d/g, '')

    this.parsedMoneyParts = {
      prefix: probe.slice(0, firstDigit),
      suffix: probe.slice(lastDigit + 1),
      thousands: separators ? separators.charAt(separators.length - 1) : '',
      decimal: decimal,
      precision: precision,
    }

    return this.parsedMoneyParts
  }

  // Mirrors the Liquid `{% render 'money-prefix' %}{{ value | money }}` output used
  // throughout the cart drawer. `dropZeroMinor` omits a zero minor unit rather than
  // stripping a trailing ".00" — formats such as `${{amount}} CAD` end in a suffix.
  renderMoney(cents, dropZeroMinor) {
    const amount = Number(cents)
    if (!Number.isFinite(amount)) return ''

    const parts = this.moneyParts()
    const [whole, minor] = (amount / 100).toFixed(parts.precision).split('.')
    const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${parts.thousands}`)
    const showMinor = minor && !(dropZeroMinor && minor === '00')

    return (
      (this.dataset.moneyPrefix || '') +
      parts.prefix +
      (showMinor ? grouped + parts.decimal + minor : grouped) +
      parts.suffix
    )
  }

  formatMoney(cents) {
    return this.renderMoney(cents, false)
  }

  formatMoneyNoDecimals(cents) {
    return this.renderMoney(Math.round(cents / 100) * 100, true)
  }

  // Discount for a set of addon products. In tag mode each product resolves
  // from its own discount tags, so a mixed set is priced per item and a product
  // carrying neither tag resolves to 0; in quantity mode the whole set shares
  // the tier `count` unlocks.
  getDiscountForCount(count, product) {
    return resolveClearanceDiscount({
      hasSelectTag: !!product?.select_tagged,
      hasBaseTag: !!product?.base_tagged,
      count: count,
    })
  }

  // Mirrors getDiscountedPrice in sections/clearance-v3.liquid: floor to the cent so
  // this matches Shopify's per-unit "each" discount allocation in the actual cart.
  getDiscountedPrice(price, percentage) {
    const discountAmount = Math.floor((price * percentage) / 100)
    return price - discountAmount
  }

  // How many clearance items are already in the cart, so the pack's predicted discount
  // reflects the tier the shopper will be at once these items are added on top.
  getClearanceCartCount() {
    try {
      const cart = document.querySelector('cart-drawer')?.cart
      if (!Array.isArray(cart?.items)) return 0
      return cart.items.reduce((total, item) => {
        return item.properties?._clearance === 'true' ? total + Number(item.quantity || 0) : total
      }, 0)
    } catch (error) {
      console.warn('CartDrawerClearanceAddon....unable to read cart clearance count', error)
      return 0
    }
  }

  // Native product JSON has no `color` field (that's a custom clearance-v3 metafield
  // lookup), so this falls back to product title for the summary line. Curated packs
  // mirror formatPackColorList()/colorsDiv on the "Quick Bundles" cards in
  // sections/clearance-v3.liquid — just the joined colors, no size line, since a pack
  // can span multiple distinct products/sizes. Accessory items aren't sized to the
  // reference item either, but being a single product their line does show their own
  // matched variant title. Singles/family append the reference item's own size, so the
  // line reads "Black · Navy · White · Size L".
  formatPackSummaryLine(products) {
    const colors = products
      .map((product) => String(product?.color || product?.title || '').trim())
      .filter(Boolean)
    const parts = Array.from(new Set(colors))

    // 'One Size' reads awkwardly as "Size One Size", so skip the prefix for it.
    function formatSizeLabel(size) {
      if (size.trim().toLowerCase() === 'one size') return size
      return `Size ${size}`
    }

    if (products.some((product) => product.pack)) {
      return parts.join(' · ')
    }

    if (products.some((product) => product.accessory)) {
      const variant = this.getVariantForSize(products[0])
      if (variant?.title && variant.title !== 'Default Title') parts.push(formatSizeLabel(variant.title))
      return parts.join(' · ')
    }

    const referenceSize = this.dataset.referenceVariantTitle
    if (referenceSize) parts.push(formatSizeLabel(referenceSize))
    return parts.join(' · ')
  }

  // Drop a trailing "N-Pack" from merchandiser/product titles so a length-1 offer
  // (curated pack with one product, or accessory) doesn't read as a multi-pack.
  stripPackSuffix(title) {
    return String(title || '').replace(/\s+\d+-Pack$/i, '').trim()
  }

  // Mirrors createPackCardElement(pack, 'style') in sections/clearance-v3.liquid, adjusted
  // to the cart drawer's horizontal reference layout (hero image + stacked thumbnails,
  // name/subtitle/price on the right, "Save $X" badge instead of a percentage).
  createBundleCardElement(products) {
    // Accessory fallback items aren't sold as a pack — no badge, no "N-Pack" suffix,
    // just the single product (which, being a 1-item array, naturally renders one
    // hero image with no stacked thumbnails below).
    const isPack = products.length > 1

    const article = document.createElement('article')
    article.className = isPack ? 'pcard pcard--style-bundle' : 'pcard pcard--style-bundle pcard--single'
    article.dataset.cartDrawerClearanceBundleCard = ''

    const imgsDiv = document.createElement('div')
    imgsDiv.className = 'pcard-imgs'

    if (isPack) {
      const badgeDiv = document.createElement('div')
      badgeDiv.className = 'pcard-badge'
      badgeDiv.textContent = `${products.length}-Pack`
      imgsDiv.appendChild(badgeDiv)
    }

    products.forEach((product, index) => {
      const imageUrl = this.resolveProductImage(product)
      if (!imageUrl) return
      const img = document.createElement('img')
      img.className = index === 0 ? 'pcard-img--hero' : 'pcard-img--sub'
      img.src = imageUrl
      img.alt = product.title || ''
      img.loading = 'lazy'
      imgsDiv.appendChild(img)
    })
    article.appendChild(imgsDiv)

    const bodyDiv = document.createElement('div')
    bodyDiv.className = 'pcard-body'

    const nameDiv = document.createElement('div')
    nameDiv.className = 'pcard-name'
    const isAccessoryPack = products.some((product) => product.accessory)
    const isCuratedPack = products.some((product) => product.pack)
    // Curated packs carry their own merchandiser-given name (mirrors pack.name in
    // createPackCardElement(), sections/clearance-v3.liquid) — used as-is for real
    // multi-item packs (the imgsDiv badge already conveys the count). For a length-1
    // offer, strip any trailing "N-Pack" so pack_name/title doesn't contradict "Add".
    if (isCuratedPack) {
      const packName = products[0]?.pack_name || 'Pack'
      nameDiv.textContent = isPack ? packName : this.stripPackSuffix(packName)
    } else {
      const familyTitle = isAccessoryPack
        ? products[0]?.family_title || products[0]?.title || 'Style'
        : this.dataset.referenceFamilyTitle || this.dataset.referenceProductTitle || 'Style'
      nameDiv.textContent = isPack
        ? `${familyTitle} ${products.length}-Pack`
        : this.stripPackSuffix(familyTitle)
    }
    bodyDiv.appendChild(nameDiv)

    const colorsDiv = document.createElement('div')
    colorsDiv.className = 'pcard-colors'
    colorsDiv.textContent = this.formatPackSummaryLine(products)
    bodyDiv.appendChild(colorsDiv)

    const totalPrice = products.reduce((total, product) => {
      const variant = this.getVariantForSize(product)
      return total + Number(variant?.price ?? product.price ?? 0)
    }, 0)

    // Discount each product individually so tag-mode sets with mixed rates add
    // up correctly; in quantity mode every product resolves to the same tier.
    const projectedCount = this.getClearanceCartCount() + products.length
    const discountedPrice = products.reduce((total, product) => {
      const variant = this.getVariantForSize(product)
      const price = Number(variant?.price ?? product.price ?? 0)
      return total + this.getDiscountedPrice(price, this.getDiscountForCount(projectedCount, product))
    }, 0)
    const saveAmount = totalPrice - discountedPrice

    const pricingDiv = document.createElement('div')
    pricingDiv.className = 'pcard-pricing'

    const nowSpan = document.createElement('span')
    nowSpan.className = 'pcard-now'
    nowSpan.textContent = this.formatMoneyNoDecimals(discountedPrice)
    pricingDiv.appendChild(nowSpan)

    const wasSpan = document.createElement('span')
    wasSpan.className = 'pcard-was'
    wasSpan.hidden = saveAmount <= 0
    wasSpan.textContent = this.formatMoney(totalPrice)
    pricingDiv.appendChild(wasSpan)

    bodyDiv.appendChild(pricingDiv)

    const saveSpan = document.createElement('span')
    saveSpan.className = 'pcard-disc'
    saveSpan.hidden = saveAmount <= 0
    saveSpan.textContent = `Save ${this.formatMoneyNoDecimals( parseInt(saveAmount/100)*100 )}`
    bodyDiv.appendChild(saveSpan)

    const addBtn = document.createElement('button')
    addBtn.className = 'pcard-btn'
    addBtn.type = 'button'
    addBtn.dataset.defaultLabel = isPack ? `Add All ${products.length}` : 'Add'
    addBtn.dataset.addedLabel = 'Added'
    addBtn.textContent = addBtn.dataset.defaultLabel
    addBtn.addEventListener('click', this.handleBundleAdd)
    this.addBtn = addBtn
    bodyDiv.appendChild(addBtn)

    const productsScript = document.createElement('script')
    productsScript.type = 'application/json'
    productsScript.dataset.cartDrawerClearanceBundleProducts = ''
    productsScript.textContent = JSON.stringify(products)
    bodyDiv.appendChild(productsScript)

    article.appendChild(bodyDiv)
    return article
  }

  renderBundle() {
    const slot = this.querySelector('[data-cart-drawer-clearance-bundle-slot]')
    if (!slot) return

    const products = this.parseProducts()
    // Accessory fallback items are sold as a single unit, not padded into a 3-pack.
    // Curated packs already come back from parseProducts() at their own full, fully
    // matched product count (mirroring "Quick Bundles") — not padded/truncated either.
    const isAccessory = products.length === 1 && products[0]?.accessory
    const isCuratedPack = products.length > 0 && products[0]?.pack
    const packProducts = isAccessory || isCuratedPack ? products : this.buildThreePack(products)

    if (!packProducts.length) {
      slot.replaceChildren()
      slot.hidden = true
      return
    }

    slot.replaceChildren(this.createBundleCardElement(packProducts))
    slot.hidden = false
  }

  // Mirrors handlePackAdd + addItemsToCart in sections/clearance-v3.liquid: add.js the
  // matched variants, then let the site-wide cartaddsuccess listener refresh the drawer.
  async handleBundleAdd(event) {
    const button = event.currentTarget
    const card = button.closest('[data-cart-drawer-clearance-bundle-card]')
    const script = card?.querySelector('[data-cart-drawer-clearance-bundle-products]')
    if (!script) return

    let packProducts = []
    try {
      packProducts = JSON.parse(script.textContent || '[]')
    } catch (error) {
      console.warn('CartDrawerClearanceAddon....unable to parse bundle products', error)
      return
    }

    const items = []
    for (const product of packProducts) {
      const variant = this.getVariantForSize(product)
      if (!variant) continue
      const existingItem = items.find((item) => item.id === variant.id)
      if (existingItem) {
        existingItem.quantity += 1
        continue
      }
      items.push({
        id: variant.id,
        quantity: 1,
        properties: {
          _clearance: 'true',
          _clearance_source: 'cart-drawer-clearance-addon',
        },
      })
    }

    if (!items.length) {
      button.textContent = 'Unavailable'
      return
    }

    button.disabled = true
    button.setAttribute('aria-busy', 'true')
    button.textContent = 'Adding...'

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      })
      const data = await response.json()
      if (!response.ok || data.status) throw new Error(data.message || 'Unable to add bundle')

      card?.classList.add('in-cart')
      button.classList.add('added')
      button.removeAttribute('aria-busy')
      button.textContent = button.dataset.addedLabel || 'Added'

      document.dispatchEvent(
        new CustomEvent('cartaddsuccess', {
          bubbles: true,
          detail: { openCart: false, refreshCart: true, variantId: items[0].id },
        })
      )
    } catch (error) {
      console.error('CartDrawerClearanceAddon....add bundle failed', error)
      button.removeAttribute('aria-busy')
      button.textContent = 'Unable to add'
    } finally {
      window.setTimeout(() => {
        button.disabled = false
        button.classList.remove('added')
        button.textContent = button.dataset.defaultLabel || 'Add All'
      }, 1400)
    }
  }
}
customElements.define('cart-drawer-clearance-addon', CartDrawerClearanceAddon)
